import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Fade,
  Tab as MuiTab,
  Tabs as MuiTabs,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { TabsProps as MuiTabProps } from '@material-ui/core/Tabs';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';

import { MenuSectionNavType } from '../../../types/menu';
import { Button } from '../../atoms';
import { ChevronRight as ChevronRightIcon } from '../../icons';
import { tabsNavUtils } from './utils';

const FADE_OFFSET = 26;

export enum ItemVariant {
  Blank = 'blank',
  Chip = 'chip',
  Tab = 'tab',
}

export interface TabsNavProps extends MuiTabProps {
  idPrefix?: string;
  itemVariant: ItemVariant;
  onTabClick?: (event: any) => void;
  tabs?: MenuSectionNavType[];
  value?: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  tabsNav: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    position: 'relative',

    '& .MuiButton-root': {
      background: 'unset',
      minWidth: 0,
      padding: 0,
    },

    '& .MuiTab-root': {
      color: theme.palette.grey[800],
      fontSize: theme.typography.pxToRem(13),
      fontWeight: theme.typography.fontWeightRegular,
      minWidth: 0,
    },
  },

  chipList: {
    '& .MuiTabs-flexContainer': {
      '& .MuiTab-root': {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[400]}`,
        borderRadius: '16px',
        boxSizing: 'border-box',
        height: '0px',
        marginRight: theme.spacing(1),
        minHeight: 'unset',
        padding: '14px',
      },
      '& .Mui-selected': {
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '& .MuiTab-wrapper': {
        display: 'inline-block',
        maxWidth: '30ch',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },

  chevron: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: 'opacity 100ms linear 0ms',
    width: '10px',
    zIndex: 1,

    '&:before': {
      content: '""',
      height: '56px',
      pointerEvents: 'none',
      position: 'absolute',
      width: '30px',
      zIndex: -1,
    },
  },

  chevronLeft: {
    '& svg': {
      transform: 'scaleX(-1)',
    },
    '&:before': {
      background: `linear-gradient(90deg, ${theme.palette.common.white} 0%, ${theme.palette.common.white} 50%, transparent 100%)`,
      left: 0,
    },
  },

  chevronRight: {
    right: 0,

    '&:before': {
      background: `linear-gradient(90deg, transparent 0%, ${theme.palette.common.white} 50%, ${theme.palette.common.white} 100%)`,
      right: 0,
    },
  },

  '@media not all and (min-resolution:.001dpcm) { @supports (-webkit-appearance:none) and (display:flow-root)':
    {
      chevronLeft: {
        '&:before': {
          background: `linear-gradient(90deg, ${theme.palette.common.white} 0%, ${theme.palette.common.white} 50%, ${theme.palette.common.white} 100%)`,
          left: 0,
        },
      },

      chevronRight: {
        '&:before': {
          background: `linear-gradient(90deg, ${theme.palette.common.white} 0%, ${theme.palette.common.white} 50%, ${theme.palette.common.white} 100%)`,
          right: 0,
        },
      },
    },
}));

export function TabsNav(props: TabsNavProps): JSX.Element {
  const {
    idPrefix,
    itemVariant,
    onTabClick,
    tabs,
    value = false,
    ...rest
  } = props;
  const { getButtonsInsideMenu, recalculateChevronVisibility } = tabsNavUtils;

  const theme = useTheme();
  const hasMobileMenu = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles();
  const scrollableWrapper = useRef(null);

  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);

  const recalculateChevronVisibilityDebounced = useCallback(
    _debounce(
      () =>
        recalculateChevronVisibility(
          scrollableWrapper,
          setShowLeftChevron,
          setShowRightChevron,
        ),
      20,
    ),
    [],
  );

  useEffect(() => {
    recalculateChevronVisibilityDebounced();
  }, []);

  if (!tabs) {
    return null;
  }

  const doHorizontalScroll = (figure: number, behavior = 'smooth') => {
    const wrapper = _get(scrollableWrapper, 'current.children[1]');
    wrapper.scrollTo({
      behavior,
      left: figure,
    });
  };

  const wrapper = _get(scrollableWrapper, 'current.children[1]');
  const wrapperSize = scrollableWrapper?.current?.offsetWidth || 0;

  const scrollMenuBar = (previousButtonClicked): void => {
    if (!wrapper) return;

    const { buttonsInside, isFirstSet } = getButtonsInsideMenu(
      wrapper,
      wrapperSize,
      previousButtonClicked,
    );

    if (previousButtonClicked) {
      // if is first button we add fadeOffset
      const [firstVisibleButton] = buttonsInside;
      const firstButtonOffset = isFirstSet ? 0 : FADE_OFFSET;
      doHorizontalScroll(firstVisibleButton.offsetLeft - firstButtonOffset);
    } else {
      const [lastVisibleButton] = buttonsInside.slice(-1);
      doHorizontalScroll(lastVisibleButton.offsetLeft - FADE_OFFSET);
    }
    recalculateChevronVisibilityDebounced();
  };

  const tabsNavStyles = clsx(
    'fd-tabsnav',
    classes.tabsNav,
    itemVariant === ItemVariant.Chip && classes.chipList,
  );
  const chevronLeftStyles = clsx(
    'fd-tabsnav__chevron-left',
    classes.chevron,
    classes.chevronLeft,
  );
  const chevronRightStyles = clsx(
    'fd-tabsnav__chevron-right',
    classes.chevron,
    classes.chevronRight,
  );

  return (
    <div className={tabsNavStyles}>
      <MuiTabs
        indicatorColor="primary"
        innerRef={scrollableWrapper}
        onScrollCapture={recalculateChevronVisibilityDebounced}
        scrollButtons="off"
        selectionFollowsFocus
        value={value}
        variant="scrollable"
        {...rest}
      >
        {tabs.map((tab) => {
          const labelText =
            typeof tab.label !== 'string' && tab.labelText
              ? tab.labelText
              : undefined;
          const isDisabled = tab?.disabled !== undefined ? tab.disabled : false;

          return (
            <MuiTab
              aria-label={labelText}
              data-fd={idPrefix && `tab-nav-${idPrefix}-${tab.id}`}
              data-id={tab.id}
              disabled={isDisabled}
              label={tab.label}
              key={tab.id}
              onClick={() => onTabClick(tab.id)}
              {...(tab.id && { value: tab.id })}
            />
          );
        })}
      </MuiTabs>

      {!hasMobileMenu && (
        <>
          <Fade in={showLeftChevron}>
            <Button
              className={chevronLeftStyles}
              onClick={(): void => scrollMenuBar(true)}
            >
              <ChevronRightIcon />
            </Button>
          </Fade>

          <Fade in={showRightChevron}>
            <Button
              className={chevronRightStyles}
              onClick={(): void => scrollMenuBar(false)}
            >
              <ChevronRightIcon />
            </Button>
          </Fade>
        </>
      )}
    </div>
  );
}
